import clsx from 'clsx'
import {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
  forwardRef,
} from 'react'
import PropTypes from 'prop-types'
import Placeholder from 'components/images/placeholder'
import useIntersection from 'components/custom-hooks/useIntersection'
import { getCompressedImageUrl, getImageSize } from './utils'

const IMAGE_RETRY_DELAY = 1000

/** Note: `imgRatio` is the value of height / width. e.g. 1280px / 720px, `imgRatio` = 1.77777778 */
const ResponsiveImage = ({
  alt,
  children,
  className,
  cy,
  fetchPriority,
  imageRef,
  imgRatio,
  imgStyle,
  lazy,
  lazyThreshold,
  loading,
  recoverOnError,
  onClick,
  src,
  style,
  wrapperProps,
  isLocalImage,
}) => {
  const ref = useRef()
  const photoRef = useRef(null)
  const setPhotoRef = useCallback(
    (node) => {
      photoRef.current = node
      if (typeof imageRef === 'function') imageRef(node)
      else if (imageRef) Object.assign(imageRef, { current: node })
    },
    [imageRef],
  )
  const [isError, setIsError] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [retryAttempt, setRetryAttempt] = useState(0)
  const [showImage, setShowImage] = useState(false)
  const [imageSize, setImageSize] = useState(null)
  let isIntersecting = useIntersection(ref, { threshold: lazyThreshold })
  if (lazy) {
    isIntersecting = true
  }

  useEffect(() => {
    if (ref.current) {
      setImageSize({
        width: ref.current.clientWidth,
        height: ref.current.clientHeight,
      })
    }
    return () => {
      setImageSize(null)
    }
  }, [])

  const imageSrc = useMemo(() => {
    if (imageSize) {
      if (isLocalImage) return src

      const size = getImageSize(imageSize)
      return getCompressedImageUrl(src, size.width, size.height)
    }
    return ''
  }, [imageSize, src, isLocalImage])

  useEffect(() => {
    if (recoverOnError) {
      setIsError(false)
      setIsLoaded(false)
      setRetryAttempt(0)
    }
  }, [imageSrc, recoverOnError])

  useEffect(() => {
    if (!recoverOnError || !isError || !imageSrc || retryAttempt >= 1) {
      return undefined
    }

    // Remount the same URL once; cancel stale retries on source changes/unmount.
    const retryTimer = setTimeout(() => {
      setRetryAttempt(1)
      setIsLoaded(false)
      setIsError(false)
    }, IMAGE_RETRY_DELAY)

    return () => clearTimeout(retryTimer)
  }, [imageSrc, isError, recoverOnError, retryAttempt])

  useEffect(() => {
    setShowImage(isIntersecting)
  }, [isIntersecting])

  // A cached/eager image can finish before the source-reset effect above.
  // Reconcile with the DOM after that reset instead of waiting for a second
  // load event that the browser will never send. Failed images also report
  // complete, so naturalWidth must be positive.
  useEffect(() => {
    const photo = photoRef.current
    if (imageSrc && photo?.complete && photo.naturalWidth > 0) {
      setIsLoaded(true)
    }
  }, [imageSrc, showImage, isError, retryAttempt, recoverOnError])

  const onLoad = useCallback(() => {
    setIsLoaded(true)
  }, [])

  const onError = useCallback(() => {
    if (recoverOnError) {
      setIsLoaded(false)
    }
    setIsError(true)
  }, [recoverOnError])

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={ref}
      className={clsx('picture', className)}
      style={style}
      onClick={onClick}
      onKeyDown={onClick}
      data-cy={cy}
      {...wrapperProps}
    >
      {!isLoaded && <Placeholder isStatic={isError} imgRatio={imgRatio} />}
      {showImage && imageSrc && !isError && (
        <img
          key={recoverOnError ? `${imageSrc}:${retryAttempt}` : undefined}
          ref={setPhotoRef}
          src={imageSrc}
          width={getImageSize(imageSize).width}
          height={getImageSize(imageSize).height}
          alt={alt}
          className={clsx('image', { 'is-loading': !isLoaded })}
          style={imgStyle}
          onError={onError}
          onLoad={onLoad}
          {...(loading && { loading })}
          {...(fetchPriority && { fetchpriority: fetchPriority })}
        />
      )}
      {children}
    </div>
  )
}

ResponsiveImage.defaultProps = {
  alt: '',
  children: null,
  className: '',
  cy: undefined,
  fetchPriority: undefined,
  imgRatio: 1,
  imageRef: null,
  imgStyle: undefined,
  lazy: true,
  lazyThreshold: 0.1,
  loading: undefined,
  recoverOnError: false,
  onClick: undefined,
  style: undefined,
  wrapperProps: undefined,
  isLocalImage: false,
}

ResponsiveImage.propTypes = {
  alt: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
    PropTypes.number,
  ]),
  className: PropTypes.string,
  fetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
  imgRatio: PropTypes.number,
  cy: PropTypes.string,
  imgStyle: PropTypes.shape({}),
  lazy: PropTypes.bool,
  lazyThreshold: PropTypes.number,
  loading: PropTypes.oneOf(['eager', 'lazy']),
  recoverOnError: PropTypes.bool,
  onClick: PropTypes.func,
  src: PropTypes.string.isRequired,
  style: PropTypes.shape({}),
  imageRef: PropTypes.func,
  wrapperProps: PropTypes.shape({}),
  isLocalImage: PropTypes.bool,
}

export default forwardRef((props, ref) => (
  <ResponsiveImage {...props} imageRef={ref} />
))
